'use client';

import { SnackbarProvider } from 'notistack';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS } from 'date-fns/locale/en-US';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ErrorBoundary from '@fuse/utils/ErrorBoundary';
import { FuseSettingsProvider } from '@fuse/core/FuseSettings/FuseSettingsProvider';
import { I18nProvider } from '@i18n/I18nProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import MainThemeProvider from '../contexts/MainThemeProvider';
import AppContext from '@/contexts/AppContext';
import { FuseDialogContextProvider } from '@fuse/core/FuseDialog/contexts/FuseDialogContext/FuseDialogContextProvider';
import { NavbarContextProvider } from '@/components/theme-layouts/components/navbar/contexts/NavbarContext/NavbarContextProvider';
import { QuickPanelProvider } from '@/components/theme-layouts/components/quickPanel/contexts/QuickPanelContext/QuickPanelContextProvider';
import RootThemeProvider from '@/contexts/RootThemeProvider';
import { NavigationContextProvider } from '@/components/theme-layouts/components/navigation/contexts/NavigationContextProvider';
import FuseLayout from '@fuse/core/FuseLayout/FuseLayout';
import themeLayouts from '@/components/theme-layouts/themeLayouts';
import MustChangePasswordModal from '@/components/MustChangePasswordModal';
import UnassignedAccessGate from '@/components/UnassignedAccessGate';
import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000, // 5 minutes
			retry: 1
		}
	}
});

const PUBLIC_ROUTES = ['/auth/login', '/auth/sign-out', '/auth/register'];
//const PUBLIC_ROUTES = ['/', '/dashboard', '/auth/login', '/auth/sign-in', '/auth/sign-out', '/auth/register', '/api/auth/login'];

type AppProps = {
	children?: React.ReactNode;
};

/**
 * The main App component.
 */
function App(props: AppProps) {
	const { children } = props;
	const AppContextValue = {};

	const pathname = usePathname();
	const router = useRouter();
	const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

	useEffect(() => {
		// Public routes don't need auth
		if (PUBLIC_ROUTES.includes(pathname ?? '')) {
		setIsAuthenticated(false);
		return;
		}

		if (typeof window !== 'undefined') {
		const token = localStorage.getItem('orchestrator_token');

		if (!token) {
			setIsAuthenticated(false);
			if (pathname !== '/auth/login') {
				router.replace('/auth/login');
			}
		} else {
			setIsAuthenticated(true);
		}
		}
	}, [pathname, router]);

	// While checking auth, don't flash the layout
	if (isAuthenticated === null && !PUBLIC_ROUTES.includes(pathname ?? '')) {
		return null;
	}

	const isPublicRoute = PUBLIC_ROUTES.includes(pathname ?? '');

	if (!isPublicRoute && isAuthenticated !== true) {
	  return null; // or render a small "Loading..." component
	}

	return (
		<ErrorBoundary>
			<AppContext value={AppContextValue}>
				{/* Date Picker Localization Provider */}
				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					adapterLocale={enUS}
				>
					<QueryClientProvider client={queryClient}>
						<FuseSettingsProvider>
							<I18nProvider>
								{/* Theme Provider */}
								<RootThemeProvider>
									<MainThemeProvider>
										{/* Public routes: render children without layout */}
										{isPublicRoute ? (
										children
										) : (
										<NavbarContextProvider>
											<NavigationContextProvider>
											<FuseDialogContextProvider>
												<SnackbarProvider
												maxSnack={5}
												anchorOrigin={{
													vertical: 'top',
													horizontal: 'right',
												}}
												classes={{
													containerRoot:
													'top-0 right-0 mt-16 mr-2 lg:mr-8 z-99',
												}}
												>
												<QuickPanelProvider>
													<UnassignedAccessGate>
														<FuseLayout layouts={themeLayouts}>{children}</FuseLayout>
													</UnassignedAccessGate>
													<MustChangePasswordModal />
												</QuickPanelProvider>
												</SnackbarProvider>
											</FuseDialogContextProvider>
											</NavigationContextProvider>
										</NavbarContextProvider>
										)}
									</MainThemeProvider>
								</RootThemeProvider>
							</I18nProvider>
						</FuseSettingsProvider>
						<ReactQueryDevtools initialIsOpen={false} />
					</QueryClientProvider>
				</LocalizationProvider>
			</AppContext>
		</ErrorBoundary>
	);
}

export default App;